import { Injectable } from '@angular/core'
import { ICandidateUpdateModel } from '@candidate/app/models/candidate-job-update.model'
import { selectors } from '@candidate/app/store/selectors'
import { environment } from '@candidate/environments/environment'
import { IFileReceipt } from '@engineering11/files-web'
import { isNotNil, omit } from '@engineering11/utility'
import { E11Logger } from '@engineering11/web-api-error'
import { RestApiClient } from '@engineering11/web-api-rest'
import { Store } from '@ngrx/store'
import { FileUploadService } from 'projects/shared-lib/src/lib/service/file-upload.service'
import { Observable, switchMap } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { getBaseSuffix, IProcessedJobApplication, TokenStorage } from 'shared-lib'
import { CandidateApplicationRepository } from './candidate-application.repository'

@Injectable({ providedIn: 'root' })
export class CandidateApplicationService {
  private restApiClient: RestApiClient
  userId$ = this.store.select(selectors.getCurrentUserId).pipe(filter(isNotNil))

  constructor(
    tokenStorage: TokenStorage,
    public repository: CandidateApplicationRepository,
    private logger: E11Logger,
    private store: Store,
    private fileUploadService: FileUploadService
  ) {
    this.restApiClient = new RestApiClient({
      baseUrl: environment.services.jobs,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'jobs'),
      enableNetworkMonitoring: true,
      networkMonitoringConfig: {
        minRequestsForAnalysis: 3,
        slowRequestThreshold: 2000,
        averageResponseTimeThreshold: 1500,
        windowSize: 120000,
      },
    })
  }

  updateCandidateApplication(applicationId: string, updates: ICandidateUpdateModel) {
    // const applicationId = this.buildJobApplicationId(jobPostId, userId)
    const sanitizedUpdates: ICandidateUpdateModel = updates.resumeAttachment
      ? { ...updates, resumeAttachment: omit(updates.resumeAttachment, 'signedURL') }
      : { ...updates }
    this.logger.log('Updating application with: ', { updates: sanitizedUpdates })
    return this.restApiClient.put(`candidate-job-application/${applicationId}`, sanitizedUpdates)
  }

  getCandidateApplication(jobPostId: string): Observable<IProcessedJobApplication | null> {
    return this.userId$.pipe(
      switchMap(userId => {
        const id = this.buildJobApplicationId(jobPostId, userId)
        return this.repository.get(id).pipe(map(item => (item ? item : null)))
      })
    )
  }

  buildJobApplicationId(jobPostId: string, userId: string) {
    return `${jobPostId}_${userId}`
  }
  async uploadResumeAttachment(file: File): Promise<IFileReceipt> {
    const response = await this.fileUploadService.uploadFile({ file: file })
    return response.fileReceipt
  }
}
